import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Divider,
  Paper,
  Stack,
  Chip,
  Button,
} from "@mui/material";
import MenuBookRoundedIcon from "@mui/icons-material/MenuBookRounded";
import LibraryMusicRoundedIcon from "@mui/icons-material/LibraryMusicRounded";
import GraphicEqRoundedIcon from "@mui/icons-material/GraphicEqRounded";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import CompareArrowsRoundedIcon from "@mui/icons-material/CompareArrowsRounded";
import ReactGA from "react-ga4";
import { Link as RouterLink } from "react-router-dom";
import { useStyle } from "../../context/styleContext/styleContext";
import Seo from "../../components/Seo";

export default function Learning() {
  const { style } = useStyle();
  const [height, setHeight] = useState(window.innerHeight);

  useEffect(() => {
    ReactGA.send({
      hitType: "pageview",
      page: "/learning",
      title: "Learning Center",
    });

    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, []);

  useEffect(() => {
    function handleResize() {
      setHeight(window.innerHeight);
    }

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const accent = "rgb(88, 166, 124)";

  const articles = [
    {
      title: "Automation in Mixing",
      description:
        "Learn how automation changes levels, effects, and plugin settings over time to create movement, clarity, and musical impact.",
      to: "/learning/automation-in-mixing",
    },
    {
      title: "Best Reverb Settings for Vocals",
      description:
        "Learn practical vocal reverb settings for plate, room, hall, decay time, pre-delay, filtering, sends, and automation.",
      to: "/learning/best-reverb-settings-for-vocals",
    },
    {
      title: "Bitcrusher / Lo-Fi",
      description:
        "Learn how bit depth reduction, sample rate reduction, and lo-fi processing add texture, grit, nostalgia, and digital character.",
      to: "/learning/bitcrusher",
    },
    {
      title: "Bus Processing",
      description:
        "Learn how bus processing works, how groups and mix buses are used, and how to add glue, tone, and control to related tracks.",
      to: "/learning/bus-processing",
    },
    {
      title: "Chorus",
      description:
        "Learn how chorus adds width, movement, and a lush ensemble-like texture to a sound.",
      to: "/learning/chorus",
    },
    {
      title: "Clipping vs Limiting",
      description:
        "Learn the difference between clipping and limiting, how each affects peaks, and when to use them in mixing and mastering.",
      to: "/learning/clipping-vs-limiting",
    },
    {
      title: "Compression",
      description:
        "Understand how compression shapes dynamics, sustain, punch, and perceived loudness.",
      to: "/learning/compression",
    },
    {
      title: "Compressor vs Limiter",
      description:
        "Learn the difference between compressors and limiters, when to use each, and how they work together.",
      to: "/learning/compressor-vs-limiter",
    },
    {
      title: "Delay",
      description:
        "Explore echoes, rhythmic repeats, slapback, feedback, and stereo delay techniques.",
      to: "/learning/delay",
    },
    {
      title: "De-Esser",
      description:
        "Learn how de-essers reduce harsh sibilance on vocals, dialogue, and other bright sources.",
      to: "/learning/de-esser",
    },
    {
      title: "Distortion",
      description:
        "See how distortion changes tone, harmonics, aggression, and character in a mix.",
      to: "/learning/distortion",
    },
    {
      title: "Dynamic EQ",
      description:
        "Learn what dynamic EQ is, how it differs from standard EQ and multi-band compression, and how it solves frequency problems only when needed.",
      to: "/learning/dynamic-eq",
    },
    {
      title: "Equalization (EQ)",
      description:
        "Learn how EQ shapes tone, clarity, balance, and space across the frequency spectrum.",
      to: "/learning/equalization",
    },
    {
      title: "Flanger",
      description:
        "Learn how flangers create sweeping, jet-like motion through short modulated delay and feedback.",
      to: "/learning/flanger",
    },
    {
      title: "Gain Staging",
      description:
        "Learn what gain staging is, why level management matters, and how proper gain structure improves recording, mixing, and mastering.",
      to: "/learning/gain-staging",
    },
    {
      title: "How to Fix a Muddy Mix",
      description:
        "Learn how to clean up low-end buildup, control low mids, tighten reverb, and make a mix clearer without making it thin.",
      to: "/learning/fix-muddy-mix",
    },
    {
      title: "How to Get Punchy Drums",
      description:
        "Learn how to make drums hit harder with better balance, phase, EQ, compression, transient shaping, saturation, clipping, and parallel processing.",
      to: "/learning/how-to-get-punchy-drums",
    },
    {
      title: "How to Keep Audio Levels Consistent",
      description:
        "Learn how to control volume changes in podcasts, voiceovers, vocals, and music using gain staging, automation, auto leveling, and compression.",
      to: "/learning/how-to-keep-audio-levels-consistent",
    },
    {
      title: "How to Make a Mix Louder Without Clipping",
      description:
        "Learn how to increase loudness cleanly with better balance, gain staging, EQ cleanup, compression, clipping, and limiting.",
      to: "/learning/how-to-make-a-mix-louder-without-clipping",
    },
    {
      title: "How to Make Vocals Sit in the Mix",
      description:
        "Learn how to keep vocals clear, steady, and connected with better leveling, EQ, compression, de-essing, space, and automation.",
      to: "/learning/make-vocals-sit-in-mix",
    },
    {
      title: "How to Tighten Low End",
      description:
        "Learn how to tighten low end in guitar, bass, and full mixes with better arrangement, EQ, compression, saturation, and low-mid control.",
      to: "/learning/how-to-tighten-low-end",
    },
    {
      title: "Limiting",
      description:
        "Learn how limiters control peaks, prevent clipping, and shape loudness in mixing and mastering.",
      to: "/learning/limiting",
    },
    {
      title: "Mastering Basics",
      description:
        "Learn what mastering is, how it differs from mixing, and how EQ, compression, limiting, and stereo imaging shape a final release.",
      to: "/learning/mastering-basics",
    },
    {
      title: "Multi-Band Compression",
      description:
        "Learn how multi-band compression works, how it differs from standard compression, and how it helps control specific frequency ranges.",
      to: "/learning/multi-band-compression",
    },
    {
      title: "Noise Gate",
      description:
        "Learn how a noise gate reduces hiss, hum, bleed, and other unwanted low-level noise.",
      to: "/learning/noise-gate",
    },
    {
      title: "Overdrive",
      description:
        "Discover the smoother side of saturation and how overdrive differs from harder clipping.",
      to: "/learning/overdrive",
    },
    {
      title: "Overdrive vs Distortion vs Fuzz",
      description:
        "Compare three classic gain styles and learn how each one affects feel, harmonics, and tone.",
      to: "/learning/compare-distortion-overdrive-fuzz",
    },
    {
      title: "Parallel Processing",
      description:
        "Learn how parallel compression, saturation, distortion, and other effects are blended with dry audio for power and control.",
      to: "/learning/parallel-processing",
    },
    {
      title: "Phaser",
      description:
        "Learn how phasers create sweeping, swirling movement through phase-based modulation.",
      to: "/learning/phaser",
    },
    {
      title: "Reverb",
      description:
        "Study ambience, space, decay, depth, and the role of reverb in production and sound design.",
      to: "/learning/reverb",
    },
    {
      title: "Saturation",
      description:
        "Learn how saturation adds harmonics, warmth, density, and analog-style character to a sound.",
      to: "/learning/saturation",
    },
    {
      title: "Sidechain Compression",
      description:
        "Learn how sidechain compression works, how ducking is used, and how sidechaining creates space, groove, and clarity in a mix.",
      to: "/learning/sidechain-compression",
    },
    {
      title: "Stereo Imaging",
      description:
        "Learn how stereo imaging shapes width, space, and placement in a mix while preserving clarity and mono compatibility.",
      to: "/learning/stereo-imaging",
    },
    {
      title: "Transient Shaper",
      description:
        "Learn how transient shapers change attack and sustain to add punch, tighten decay, and reshape feel.",
      to: "/learning/transient-shaper",
    },
    {
      title: "Tremolo",
      description:
        "Learn how tremolo creates rhythmic movement by modulating volume over time.",
      to: "/learning/tremolo",
    },
    {
      title: "Vibrato",
      description:
        "Learn what vibrato is, how pitch modulation works, and how vibrato differs from tremolo and chorus in music production.",
      to: "/learning/vibrato",
    },
    {
      title: "Vocal Processing Chain Explained",
      description:
        "Learn how a vocal chain works, including editing, leveling, EQ, compression, de-essing, saturation, reverb, delay, and automation.",
      to: "/learning/vocal-processing-chain",
    },
  ];

  const collectionSchema = useMemo(
    () => ({
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: "Parish Audio Learning Center",
      description:
        "Educational articles about audio effects, mixing, sound design, and music production from Parish Audio.",
      url: "https://parishaudio.com/learning",
      hasPart: articles.map((article) => ({
        "@type": "Article",
        name: article.title,
        url: `https://parishaudio.com${article.to}`,
      })),
    }),
    [articles]
  );

  const featuredTopics = [
    {
      title: "How to Fix a Muddy Mix",
      description:
        "Learn how to clean up low-end buildup, control low mids, and make a mix clearer without making it thin.",
      to: "/learning/fix-muddy-mix",
    },
    {
      title: "How to Make Vocals Sit in the Mix",
      description:
        "Learn how to keep vocals clear, steady, and connected with better leveling, EQ, compression, de-essing, space, and automation.",
      to: "/learning/make-vocals-sit-in-mix",
    },
    {
      title: "Overdrive vs Distortion vs Fuzz",
      description:
        "Compare three classic gain styles and learn how each one affects feel, harmonics, and tone.",
      to: "/learning/compare-distortion-overdrive-fuzz",
    },
  ];

  const highlights = [
    {
      title: "Audio effect basics",
      text: "Clear explanations of core effects used in mixing, production, and sound design.",
      icon: <GraphicEqRoundedIcon sx={{ color: accent }} />,
    },
    {
      title: "Practical concepts",
      text: "Learn what controls do, when to use them, and how they affect real-world sound.",
      icon: <LibraryMusicRoundedIcon sx={{ color: accent }} />,
    },
    {
      title: "Problem-solving guides",
      text: "Explore real mixing problems like muddy mixes, uneven vocals, low-end control, loudness, and drum punch.",
      icon: <MenuBookRoundedIcon sx={{ color: accent }} />,
    },
  ];

  const outerPanelSx = {
    width: "100%",
    minWidth: 0,
    borderRadius: "20px",
    overflow: "hidden",
    backgroundColor: style.color1,
    border: `1px solid ${style.color2}`,
    boxSizing: "border-box",
  };

  const innerPanelSx = {
    borderRadius: "16px",
    backgroundColor: style.darkPanel,
    border: "1px solid rgba(255,255,255,0.08)",
    boxShadow: "none",
  };

  const subtlePanelSx = {
    borderRadius: "14px",
    backgroundColor: style.darkerPanel,
    border: "1px solid rgba(255,255,255,0.06)",
    boxShadow: "none",
  };

  return (
    <>
      <Seo
        title="Learning Center | Parish Audio"
        description="Explore educational articles from Parish Audio covering audio effects, mixing, sound design, vocals, guitar tone, loudness, and music production topics."
        canonical="https://parishaudio.com/learning"
        image="https://parishaudio.com/RecordingStudioStockImage1.jpg"
        imageAlt="Recording studio background for the Parish Audio Learning Center"
        schema={collectionSchema}
      />

      <Box
        sx={{
          width: "100%",
          maxWidth: "100vw",
          overflowX: "hidden",
          backgroundColor: "black",
          px: { xs: 1.25, sm: 1.5, md: 2.5 },
          py: { xs: 1.5, md: 2.5 },
          boxSizing: "border-box",
          minHeight: height - 130,
        }}
      >
        <Box sx={{ width: "100%", maxWidth: 1120, mx: "auto", minWidth: 0 }}>
          <Stack spacing={{ xs: 2, md: 2.5 }}>
            <Paper
              elevation={0}
              sx={{
                ...outerPanelSx,
                minHeight: { xs: 250, md: 300 },
                display: "flex",
                alignItems: "center",
                backgroundImage: `linear-gradient(90deg, rgba(8,12,18,0.82) 0%, rgba(8,12,18,0.72) 38%, rgba(8,12,18,0.40) 68%, rgba(8,12,18,0.20) 100%), url("/RecordingStudioStockImage1.jpg")`,
                backgroundSize: "cover",
                backgroundPosition: "center",
              }}
            >
              <Box sx={{ p: { xs: 2, sm: 3, md: 4 }, width: "100%" }}>
                <Box sx={{ maxWidth: 700 }}>
                  <Stack spacing={1.25} alignItems="flex-start">
                    <Chip
                      label="Learning Center"
                      sx={{
                        borderRadius: "5px",
                        fontWeight: 700,
                        color: style.color4,
                        backgroundColor: "rgba(255,255,255,0.08)",
                        border: "1px solid rgba(255,255,255,0.10)",
                      }}
                    />

                    <Typography
                      variant="h1"
                      sx={{
                        fontSize: { xs: "2.1rem", md: "3.35rem" },
                        fontWeight: 800,
                        lineHeight: 1.02,
                        color: style.color4,
                        letterSpacing: "-0.04em",
                      }}
                    >
                      Learn audio effects
                      <br />
                      and production concepts
                    </Typography>

                    <Typography
                      sx={{
                        color: "rgba(255,255,255,0.86)",
                        fontSize: { xs: "1rem", md: "1.1rem" },
                        lineHeight: 1.75,
                        maxWidth: 620,
                      }}
                    >
                      Explore educational articles about audio effects, tone shaping,
                      mixing, sound design, and music production from Parish Audio.
                    </Typography>
                  </Stack>
                </Box>
              </Box>
            </Paper>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "repeat(3, minmax(0, 1fr))" },
                gap: 2,
                alignItems: "stretch",
              }}
            >
              {highlights.map((item) => (
                <Paper
                  key={item.title}
                  elevation={0}
                  sx={{
                    ...outerPanelSx,
                    p: 0,
                    overflow: "hidden",
                  }}
                >
                  <Box sx={{ p: 2 }}>
                    <Stack direction="row" spacing={1.25} alignItems="flex-start">
                      {item.icon}
                      <Box sx={{ minWidth: 0 }}>
                        <Typography
                          sx={{
                            fontWeight: 700,
                            color: style.color4,
                            mb: 0.4,
                          }}
                        >
                          {item.title}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ color: style.color3, lineHeight: 1.65 }}
                        >
                          {item.text}
                        </Typography>
                      </Box>
                    </Stack>
                  </Box>
                </Paper>
              ))}
            </Box>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "1.25fr 0.75fr" },
                gap: 2,
                alignItems: "start",
              }}
            >
              <Paper elevation={0} sx={outerPanelSx}>
                <Box
                  sx={{
                    px: { xs: 1.5, sm: 2, md: 2.5 },
                    py: { xs: 1.5, md: 1.75 },
                    backgroundColor: style.darkPanel,
                    borderBottom: `1px solid ${style.color2}`,
                  }}
                >
                  <Stack direction="row" spacing={1} alignItems="center">
                    <AutoAwesomeRoundedIcon sx={{ color: accent }} />
                    <Typography
                      sx={{
                        color: style.color4,
                        fontWeight: 800,
                        fontSize: { xs: "1.25rem", md: "1.5rem" },
                      }}
                    >
                      Articles
                    </Typography>
                  </Stack>
                </Box>

                <Box sx={{ p: { xs: 1.25, md: 1.5 } }}>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" },
                      gap: 2,
                      alignItems: "stretch",
                    }}
                  >
                    {articles.map((item) => (
                      <Paper
                        key={item.to}
                        elevation={0}
                        sx={{
                          ...innerPanelSx,
                          p: 2,
                          minWidth: 0,
                        }}
                      >
                        <Stack spacing={1.1} sx={{ height: "100%" }}>
                          <Typography
                            sx={{
                              fontWeight: 700,
                              color: style.color4,
                              fontSize: "1.05rem",
                            }}
                          >
                            {item.title}
                          </Typography>

                          <Typography
                            variant="body2"
                            sx={{
                              color: style.color3,
                              lineHeight: 1.65,
                              flexGrow: 1,
                            }}
                          >
                            {item.description}
                          </Typography>

                          <Button
                            component={RouterLink}
                            to={item.to}
                            sx={{
                              alignSelf: "flex-start",
                              mt: "auto",
                              borderRadius: "5px",
                              textTransform: "none",
                              fontWeight: 700,
                              color: style.color4,
                              backgroundColor: "rgba(255,255,255,0.05)",
                              border: "1px solid rgba(255,255,255,0.10)",
                              "&:hover": {
                                backgroundColor: "rgba(255,255,255,0.10)",
                              },
                            }}
                          >
                            Read Article
                          </Button>
                        </Stack>
                      </Paper>
                    ))}
                  </Box>
                </Box>
              </Paper>

              <Paper elevation={0} sx={outerPanelSx}>
                <Box
                  sx={{
                    px: { xs: 1.5, sm: 2, md: 2.5 },
                    py: { xs: 1.5, md: 1.75 },
                    backgroundColor: style.darkPanel,
                    borderBottom: `1px solid ${style.color2}`,
                  }}
                >
                  <Stack direction="row" spacing={1} alignItems="center">
                    <CompareArrowsRoundedIcon sx={{ color: accent }} />
                    <Typography
                      sx={{
                        color: style.color4,
                        fontWeight: 800,
                        fontSize: { xs: "1.25rem", md: "1.5rem" },
                      }}
                    >
                      Featured Topics
                    </Typography>
                  </Stack>
                </Box>

                <Box sx={{ p: { xs: 1.25, md: 1.5 } }}>
                  <Stack spacing={1.5}>
                    {featuredTopics.map((item) => (
                      <Paper key={item.to} elevation={0} sx={{ ...innerPanelSx, p: 2 }}>
                        <Stack spacing={1.1}>
                          <Typography
                            sx={{
                              fontWeight: 700,
                              color: style.color4,
                              fontSize: "1.05rem",
                              lineHeight: 1.4,
                            }}
                          >
                            {item.title}
                          </Typography>

                          <Typography
                            variant="body2"
                            sx={{ color: style.color3, lineHeight: 1.65 }}
                          >
                            {item.description}
                          </Typography>

                          <Button
                            component={RouterLink}
                            to={item.to}
                            sx={{
                              alignSelf: "flex-start",
                              borderRadius: "5px",
                              textTransform: "none",
                              fontWeight: 700,
                              color: style.color4,
                              backgroundColor: style.accentGreen,
                              "&:hover": {
                                backgroundColor: style.accentGreen,
                                filter: "brightness(1.05)",
                              },
                            }}
                          >
                            Read Topic
                          </Button>
                        </Stack>
                      </Paper>
                    ))}

                    <Divider sx={{ backgroundColor: "rgba(255,255,255,0.08)" }} />

                    <Paper elevation={0} sx={{ ...subtlePanelSx, p: 1.5 }}>
                      <Typography sx={{ color: style.color3, lineHeight: 1.7 }}>
                        New guides are added over time as the library grows, with more focus
                        on real mixing problems, practical workflows, and tools you can use
                        in actual sessions.
                      </Typography>
                    </Paper>
                  </Stack>
                </Box>
              </Paper>
            </Box>

            <Paper elevation={0} sx={outerPanelSx}>
              <Box sx={{ p: { xs: 1.25, md: 1.5 } }}>
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={1.5}
                  justifyContent="space-between"
                  alignItems={{ xs: "flex-start", sm: "center" }}
                >
                  <Typography sx={{ color: style.color3, lineHeight: 1.7 }}>
                    You can also browse Parish Audio plugins or return to the home page.
                  </Typography>

                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    <Button
                      component={RouterLink}
                      to="/plugins"
                      sx={{
                        borderRadius: "5px",
                        textTransform: "none",
                        fontWeight: 700,
                        color: style.color4,
                        backgroundColor: style.accentGreen,
                        "&:hover": {
                          backgroundColor: style.accentGreen,
                          filter: "brightness(1.05)",
                        },
                      }}
                    >
                      Plugins
                    </Button>

                    <Button
                      component={RouterLink}
                      to="/"
                      sx={{
                        borderRadius: "5px",
                        textTransform: "none",
                        fontWeight: 700,
                        color: style.color4,
                        backgroundColor: "rgba(255,255,255,0.05)",
                        border: "1px solid rgba(255,255,255,0.10)",
                        "&:hover": {
                          backgroundColor: "rgba(255,255,255,0.10)",
                        },
                      }}
                    >
                      Home
                    </Button>
                  </Stack>
                </Stack>
              </Box>
            </Paper>
          </Stack>
        </Box>
      </Box>
    </>
  );
}


// import React, { useEffect, useMemo, useState } from "react";
// import {
//   Box,
//   Typography,
//   Divider,
//   Paper,
//   Stack,
//   Chip,
//   Button,
// } from "@mui/material";
// import MenuBookRoundedIcon from "@mui/icons-material/MenuBookRounded";
// import LibraryMusicRoundedIcon from "@mui/icons-material/LibraryMusicRounded";
// import GraphicEqRoundedIcon from "@mui/icons-material/GraphicEqRounded";
// import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
// import CompareArrowsRoundedIcon from "@mui/icons-material/CompareArrowsRounded";
// import ReactGA from "react-ga4";
// import { Link as RouterLink } from "react-router-dom";
// import { useStyle } from "../../context/styleContext/styleContext";
// import Seo from "../../components/Seo";

// export default function Learning() {
//   const { style } = useStyle();
//   const [height, setHeight] = useState(window.innerHeight);

//   useEffect(() => {
//     ReactGA.send({
//       hitType: "pageview",
//       page: "/learning",
//       title: "Learning Center",
//     });

//     window.scrollTo({ top: 0, left: 0, behavior: "auto" });
//   }, []);

//   useEffect(() => {
//     function handleResize() {
//       setHeight(window.innerHeight);
//     }

//     window.addEventListener("resize", handleResize);
//     return () => window.removeEventListener("resize", handleResize);
//   }, []);

//   const accent = "rgb(88, 166, 124)";

//   const collectionSchema = useMemo(
//     () => ({
//       "@context": "https://schema.org",
//       "@type": "CollectionPage",
//       name: "Parish Audio Learning Center",
//       description:
//         "Educational articles about audio effects, mixing, sound design, and music production from Parish Audio.",
//       url: "https://parishaudio.com/learning",
//       hasPart: [
//         { "@type": "Article", name: "Chorus", url: "https://parishaudio.com/learning/chorus" },
//         { "@type": "Article", name: "Compression", url: "https://parishaudio.com/learning/compression" },
//         { "@type": "Article", name: "Delay", url: "https://parishaudio.com/learning/delay" },
//         { "@type": "Article", name: "Distortion", url: "https://parishaudio.com/learning/distortion" },
//         { "@type": "Article", name: "Equalization", url: "https://parishaudio.com/learning/equalization" },
//         { "@type": "Article", name: "Noise Gate", url: "https://parishaudio.com/learning/noise-gate" },
//         { "@type": "Article", name: "De-Esser", url: "https://parishaudio.com/learning/de-esser" },
//         { "@type": "Article", name: "Phaser", url: "https://parishaudio.com/learning/phaser" },
//         { "@type": "Article", name: "Limiting", url: "https://parishaudio.com/learning/limiting" },
//         { "@type": "Article", name: "Overdrive", url: "https://parishaudio.com/learning/overdrive" },
//         { "@type": "Article", name: "Reverb", url: "https://parishaudio.com/learning/reverb" },
//         { "@type": "Article", name: "Saturation", url: "https://parishaudio.com/learning/saturation" },
//         {
//           "@type": "Article",
//           name: "Overdrive vs Distortion vs Fuzz: What’s the Difference?",
//           url: "https://parishaudio.com/learning/compare-distortion-overdrive-fuzz",
//         },
//         { "@type": "Article", name: "Flanger", url: "https://parishaudio.com/learning/flanger" },
//         { "@type": "Article", name: "Tremolo", url: "https://parishaudio.com/learning/tremolo" },
//         { "@type": "Article", name: "Stereo Imaging", url: "https://parishaudio.com/learning/stereo-imaging" },
//         { "@type": "Article", name: "Transient Shaper", url: "https://parishaudio.com/learning/transient-shaper" },
//         { "@type": "Article", name: "Mastering Basics", url: "https://parishaudio.com/learning/mastering-basics" },
//         { "@type": "Article", name: "Compressor vs Limiter", url: "https://parishaudio.com/learning/compressor-vs-limiter" },
      
//         {
//           "@type": "ListItem",
//           name: "Vibrato",
//           url: "https://parishaudio.com/learning/vibrato",
//         },
//         {
//           "@type": "ListItem",
//           name: "Gain Staging",
//           url: "https://parishaudio.com/learning/gain-staging",
//         },
//         {
//           "@type": "ListItem",
//           name: "Bitcrusher / Lo-Fi",
//           url: "https://parishaudio.com/learning/bitcrusher",
//         },
//         {
//           "@type": "ListItem",
//           name: "Multi-Band Compression",
//           url: "https://parishaudio.com/learning/multi-band-compression",
//         },
//         {
//           "@type": "ListItem",
//           name: "Bus Processing",
//           url: "https://parishaudio.com/learning/bus-processing",
//         },
//         {
//           "@type": "ListItem",
//           name: "Clipping vs Limiting",
//           url: "https://parishaudio.com/learning/clipping-vs-limiting",
//         },
//         {
//           "@type": "ListItem",
//           name: "Parallel Processing",
//           url: "https://parishaudio.com/learning/parallel-processing",
//         },
//         {
//           "@type": "ListItem",
//           name: "Automation in Mixing",
//           url: "https://parishaudio.com/learning/automation-in-mixing",
//         },
//         {
//           "@type": "ListItem",
//           name: "Sidechain Compression",
//           url: "https://parishaudio.com/learning/sidechain-compression",
//         },
//         {
//           "@type": "ListItem",
//           name: "Dynamic EQ",
//           url: "https://parishaudio.com/learning/dynamic-eq",
//         },
      
//       ],
//     }),
//     []
//   );

//   const categories = [
//     {
//       title: "Chorus",
//       description:
//         "Learn how chorus adds width, movement, and a lush ensemble-like texture to a sound.",
//       to: "/learning/chorus",
//     },
//     {
//       title: "Compression",
//       description:
//         "Understand how compression shapes dynamics, sustain, punch, and perceived loudness.",
//       to: "/learning/compression",
//     },
//     {
//       title: "Delay",
//       description:
//         "Explore echoes, rhythmic repeats, slapback, feedback, and stereo delay techniques.",
//       to: "/learning/delay",
//     },
//     {
//       title: "Compressor vs Limiter",
//       description:
//         "Learn the difference between compressors and limiters, when to use each, and how they work together.",
//       to: "/learning/compressor-vs-limiter",
//     },
//     {
//       title: "Distortion",
//       description:
//         "See how distortion changes tone, harmonics, aggression, and character in a mix.",
//       to: "/learning/distortion",
//     },
//     {
//       title: "Equalization (EQ)",
//       description:
//         "Learn how EQ shapes tone, clarity, balance, and space across the frequency spectrum.",
//       to: "/learning/equalization",
//     },
//     {
//       title: "Noise Gate",
//       description:
//         "Learn how a noise gate reduces hiss, hum, bleed, and other unwanted low-level noise.",
//       to: "/learning/noise-gate",
//     },
//     {
//       title: "De-Esser",
//       description:
//         "Learn how de-essers reduce harsh sibilance on vocals, dialogue, and other bright sources.",
//       to: "/learning/de-esser",
//     },
//     {
//       title: "Phaser",
//       description:
//         "Learn how phasers create sweeping, swirling movement through phase-based modulation.",
//       to: "/learning/phaser",
//     },
//     {
//       title: "Limiting",
//       description:
//         "Learn how limiters control peaks, prevent clipping, and shape loudness in mixing and mastering.",
//       to: "/learning/limiting",
//     },
//     {
//       title: "Overdrive",
//       description:
//         "Discover the smoother side of saturation and how overdrive differs from harder clipping.",
//       to: "/learning/overdrive",
//     },
//     {
//       title: "Reverb",
//       description:
//         "Study ambience, space, decay, depth, and the role of reverb in production and sound design.",
//       to: "/learning/reverb",
//     },
//     {
//       title: "Saturation",
//       description:
//         "Learn how saturation adds harmonics, warmth, density, and analog-style character to a sound.",
//       to: "/learning/saturation",
//     },
//     {
//       title: "Stereo Imaging",
//       description:
//         "Learn how stereo imaging shapes width, space, and placement in a mix while preserving clarity and mono compatibility.",
//       to: "/learning/stereo-imaging",
//     },
//     {
//       title: "Flanger",
//       description:
//         "Learn how flangers create sweeping, jet-like motion through short modulated delay and feedback.",
//       to: "/learning/flanger",
//     },
//     {
//       title: "Tremolo",
//       description:
//         "Learn how tremolo creates rhythmic movement by modulating volume over time.",
//       to: "/learning/tremolo",
//     },
//     {
//       title: "Transient Shaper",
//       description:
//         "Learn how transient shapers change attack and sustain to add punch, tighten decay, and reshape feel.",
//       to: "/learning/transient-shaper",
//     },
//     {
//       title: "Mastering Basics",
//       description:
//         "Learn what mastering is, how it differs from mixing, and how EQ, compression, limiting, and stereo imaging shape a final release.",
//       to: "/learning/mastering-basics",
//     },
//     {
//       title: "Vibrato",
//       description: "Learn what vibrato is, how pitch modulation works, and how vibrato differs from tremolo and chorus in music production.",
//       to: "/learning/vibrato",
//     },
//     {
//       title: "Gain Staging",
//       description: "Learn what gain staging is, why level management matters, and how proper gain structure improves recording, mixing, and mastering.",
//       to: "/learning/gain-staging",
//     },
//     {
//       title: "Bitcrusher / Lo-Fi",
//       description: "Learn how bit depth reduction, sample rate reduction, and lo-fi processing add texture, grit, nostalgia, and digital character.",
//       to: "/learning/bitcrusher-lofi",
//     },
//     {
//       title: "Multi-Band Compression",
//       description: "Learn how multi-band compression works, how it differs from standard compression, and how it helps control specific frequency ranges.",
//       to: "/learning/multi-band-compression",
//     },
//     {
//       title: "Bus Processing",
//       description: "Learn how bus processing works, how groups and mix buses are used, and how to add glue, tone, and control to related tracks.",
//       to: "/learning/bus-processing",
//     },
//     {
//       title: "Clipping vs Limiting",
//       description: "Learn the difference between clipping and limiting, how each affects peaks, and when to use them in mixing and mastering.",
//       to: "/learning/clipping-vs-limiting",
//     },
//     {
//       title: "Parallel Processing",
//       description: "Learn how parallel compression, saturation, distortion, and other effects are blended with dry audio for power and control.",
//       to: "/learning/parallel-processing",
//     },
//     {
//       title: "Automation in Mixing",
//       description: "Learn how automation changes levels, effects, and plugin settings over time to create movement, clarity, and musical impact.",
//       to: "/learning/automation-in-mixing",
//     },
//     {
//       title: "Sidechain Compression",
//       description: "Learn how sidechain compression works, how ducking is used, and how sidechaining creates space, groove, and clarity in a mix.",
//       to: "/learning/sidechain-compression",
//     },
//     {
//       title: "Dynamic EQ",
//       description: "Learn what dynamic EQ is, how it differs from standard EQ and multi-band compression, and how it solves frequency problems only when needed.",
//       to: "/learning/dynamic-eq",
//     },
//     {
//       title: "How to Fix a Muddy Mix",
//       description:
//         "Learn how to clean up low-end buildup, control low mids, tighten reverb, and make a mix clearer without making it thin.",
//       to: "/learning/fix-muddy-mix",
//     },
//     {
//       title: "How to Make Vocals Sit in the Mix",
//       description:
//         "Learn how to keep vocals clear, steady, and connected with better leveling, EQ, compression, de-essing, space, and automation.",
//       to: "/learning/make-vocals-sit-in-mix",
//     },
//     {
//       title: "How to Keep Audio Levels Consistent",
//       description:
//         "Learn how to control volume changes in podcasts, voiceovers, vocals, and music using gain staging, automation, auto leveling, and compression.",
//       to: "/learning/how-to-keep-audio-levels-consistent",
//     },
//     {
//       title: "How to Get Punchy Drums",
//       description:
//         "Learn how to make drums hit harder with better balance, phase, EQ, compression, transient shaping, saturation, clipping, and parallel processing.",
//       to: "/learning/how-to-get-punchy-drums",
//     },
//     {
//       title: "How to Tighten Low End",
//       description:
//         "Learn how to tighten low end in guitar, bass, and full mixes with better arrangement, EQ, compression, saturation, and low-mid control.",
//       to: "/learning/how-to-tighten-low-end",
//     },
//     {
//       title: "How to Make a Mix Louder Without Clipping",
//       description:
//         "Learn how to increase loudness cleanly with better balance, gain staging, EQ cleanup, compression, clipping, and limiting.",
//       to: "/learning/how-to-make-a-mix-louder-without-clipping",
//     },
//     {
//       title: "Vocal Processing Chain Explained",
//       description:
//         "Learn how a vocal chain works, including editing, leveling, EQ, compression, de-essing, saturation, reverb, delay, and automation.",
//       to: "/learning/vocal-processing-chain",
//     },
//     {
//       title: "Best Reverb Settings for Vocals",
//       description:
//         "Learn practical vocal reverb settings for plate, room, hall, decay time, pre-delay, filtering, sends, and automation.",
//       to: "/learning/best-reverb-settings-for-vocals",
//     },
//   ];


//   const featuredTopics = [
//     {
//       title: "Overdrive vs Distortion vs Fuzz: What’s the Difference?",
//       description:
//         "Compare three classic gain styles and learn how each one affects feel, harmonics, and tone.",
//       to: "/learning/compare-distortion-overdrive-fuzz",
//     },
//   ];

//   const highlights = [
//     {
//       title: "Audio effect basics",
//       text: "Clear explanations of core effects used in mixing, production, and sound design.",
//       icon: <GraphicEqRoundedIcon sx={{ color: accent }} />,
//     },
//     {
//       title: "Practical concepts",
//       text: "Learn what controls do, when to use them, and how they affect real-world sound.",
//       icon: <LibraryMusicRoundedIcon sx={{ color: accent }} />,
//     },
//     {
//       title: "Easy navigation",
//       text: "Browse by effect type or jump straight into focused comparison topics.",
//       icon: <MenuBookRoundedIcon sx={{ color: accent }} />,
//     },
//   ];

//   const outerPanelSx = {
//     width: "100%",
//     minWidth: 0,
//     borderRadius: "20px",
//     overflow: "hidden",
//     backgroundColor: style.color1,
//     border: `1px solid ${style.color2}`,
//     boxSizing: "border-box",
//   };

//   const innerPanelSx = {
//     borderRadius: "16px",
//     backgroundColor: style.darkPanel,
//     border: "1px solid rgba(255,255,255,0.08)",
//     boxShadow: "none",
//   };

//   const subtlePanelSx = {
//     borderRadius: "14px",
//     backgroundColor: style.darkerPanel,
//     border: "1px solid rgba(255,255,255,0.06)",
//     boxShadow: "none",
//   };

//   return (
//     <>
//       <Seo
//         title="Learning Center | Parish Audio"
//         description="Explore educational articles from Parish Audio covering chorus, compression, delay, distortion, EQ, overdrive, reverb, and related music production topics."
//         canonical="https://parishaudio.com/learning"
//         image="https://parishaudio.com/RecordingStudioStockImage1.jpg"
//         imageAlt="Recording studio background for the Parish Audio Learning Center"
//         schema={collectionSchema}
//       />

//       <Box
//         sx={{
//           width: "100%",
//           maxWidth: "100vw",
//           overflowX: "hidden",
//           backgroundColor: "black",
//           px: { xs: 1.25, sm: 1.5, md: 2.5 },
//           py: { xs: 1.5, md: 2.5 },
//           boxSizing: "border-box",
//           minHeight: height - 130,
//         }}
//       >
//         <Box sx={{ width: "100%", maxWidth: 1120, mx: "auto", minWidth: 0 }}>
//           <Stack spacing={{ xs: 2, md: 2.5 }}>
//             <Paper
//               elevation={0}
//               sx={{
//                 ...outerPanelSx,
//                 minHeight: { xs: 250, md: 300 },
//                 display: "flex",
//                 alignItems: "center",
//                 backgroundImage: `linear-gradient(90deg, rgba(8,12,18,0.82) 0%, rgba(8,12,18,0.72) 38%, rgba(8,12,18,0.40) 68%, rgba(8,12,18,0.20) 100%), url("/RecordingStudioStockImage1.jpg")`,
//                 backgroundSize: "cover",
//                 backgroundPosition: "center",
//               }}
//             >
//               <Box sx={{ p: { xs: 2, sm: 3, md: 4 }, width: "100%" }}>
//                 <Box sx={{ maxWidth: 700 }}>
//                   <Stack spacing={1.25} alignItems="flex-start">
//                     <Chip
//                       label="Learning Center"
//                       sx={{
//                         borderRadius: "5px",
//                         fontWeight: 700,
//                         color: style.color4,
//                         backgroundColor: "rgba(255,255,255,0.08)",
//                         border: "1px solid rgba(255,255,255,0.10)",
//                       }}
//                     />

//                     <Typography
//                       variant="h1"
//                       sx={{
//                         fontSize: { xs: "2.1rem", md: "3.35rem" },
//                         fontWeight: 800,
//                         lineHeight: 1.02,
//                         color: style.color4,
//                         letterSpacing: "-0.04em",
//                       }}
//                     >
//                       Learn audio effects
//                       <br />
//                       and production concepts
//                     </Typography>

//                     <Typography
//                       sx={{
//                         color: "rgba(255,255,255,0.86)",
//                         fontSize: { xs: "1rem", md: "1.1rem" },
//                         lineHeight: 1.75,
//                         maxWidth: 620,
//                       }}
//                     >
//                       Explore educational articles about audio effects, tone shaping,
//                       mixing, sound design, and music production from Parish Audio.
//                     </Typography>
//                   </Stack>
//                 </Box>
//               </Box>
//             </Paper>

//             <Box
//               sx={{
//                 display: "grid",
//                 gridTemplateColumns: { xs: "1fr", md: "repeat(3, minmax(0, 1fr))" },
//                 gap: 2,
//                 alignItems: "stretch",
//               }}
//             >
//               {highlights.map((item) => (
//                 <Paper
//                   key={item.title}
//                   elevation={0}
//                   sx={{
//                     ...outerPanelSx,
//                     p: 0,
//                     overflow: "hidden",
//                   }}
//                 >
//                   <Box sx={{ p: 2 }}>
//                     <Stack direction="row" spacing={1.25} alignItems="flex-start">
//                       {item.icon}
//                       <Box sx={{ minWidth: 0 }}>
//                         <Typography
//                           sx={{
//                             fontWeight: 700,
//                             color: style.color4,
//                             mb: 0.4,
//                           }}
//                         >
//                           {item.title}
//                         </Typography>
//                         <Typography
//                           variant="body2"
//                           sx={{ color: style.color3, lineHeight: 1.65 }}
//                         >
//                           {item.text}
//                         </Typography>
//                       </Box>
//                     </Stack>
//                   </Box>
//                 </Paper>
//               ))}
//             </Box>

//             <Box
//               sx={{
//                 display: "grid",
//                 gridTemplateColumns: { xs: "1fr", md: "1.25fr 0.75fr" },
//                 gap: 2,
//                 alignItems: "start",
//               }}
//             >
//               <Paper elevation={0} sx={outerPanelSx}>
//                 <Box
//                   sx={{
//                     px: { xs: 1.5, sm: 2, md: 2.5 },
//                     py: { xs: 1.5, md: 1.75 },
//                     backgroundColor: style.darkPanel,
//                     borderBottom: `1px solid ${style.color2}`,
//                   }}
//                 >
//                   <Stack direction="row" spacing={1} alignItems="center">
//                     <AutoAwesomeRoundedIcon sx={{ color: accent }} />
//                     <Typography
//                       sx={{
//                         color: style.color4,
//                         fontWeight: 800,
//                         fontSize: { xs: "1.25rem", md: "1.5rem" },
//                       }}
//                     >
//                       Categories
//                     </Typography>
//                   </Stack>
//                 </Box>

//                 <Box sx={{ p: { xs: 1.25, md: 1.5 } }}>
//                   <Box
//                     sx={{
//                       display: "grid",
//                       gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" },
//                       gap: 2,
//                       alignItems: "stretch",
//                     }}
//                   >
//                     {categories.map((item) => (
//                       <Paper
//                         key={item.to}
//                         elevation={0}
//                         sx={{
//                           ...innerPanelSx,
//                           p: 2,
//                           minWidth: 0,
//                         }}
//                       >
//                         <Stack spacing={1.1} sx={{ height: "100%" }}>
//                           <Typography
//                             sx={{
//                               fontWeight: 700,
//                               color: style.color4,
//                               fontSize: "1.05rem",
//                             }}
//                           >
//                             {item.title}
//                           </Typography>

//                           <Typography
//                             variant="body2"
//                             sx={{
//                               color: style.color3,
//                               lineHeight: 1.65,
//                               flexGrow: 1,
//                             }}
//                           >
//                             {item.description}
//                           </Typography>

//                           <Button
//                             component={RouterLink}
//                             to={item.to}
//                             sx={{
//                               alignSelf: "flex-start",
//                               mt: "auto",
//                               borderRadius: "5px",
//                               textTransform: "none",
//                               fontWeight: 700,
//                               color: style.color4,
//                               backgroundColor: "rgba(255,255,255,0.05)",
//                               border: "1px solid rgba(255,255,255,0.10)",
//                               "&:hover": {
//                                 backgroundColor: "rgba(255,255,255,0.10)",
//                               },
//                             }}
//                           >
//                             Read Article
//                           </Button>
//                         </Stack>
//                       </Paper>
//                     ))}
//                   </Box>
//                 </Box>
//               </Paper>

//               <Paper elevation={0} sx={outerPanelSx}>
//                 <Box
//                   sx={{
//                     px: { xs: 1.5, sm: 2, md: 2.5 },
//                     py: { xs: 1.5, md: 1.75 },
//                     backgroundColor: style.darkPanel,
//                     borderBottom: `1px solid ${style.color2}`,
//                   }}
//                 >
//                   <Stack direction="row" spacing={1} alignItems="center">
//                     <CompareArrowsRoundedIcon sx={{ color: accent }} />
//                     <Typography
//                       sx={{
//                         color: style.color4,
//                         fontWeight: 800,
//                         fontSize: { xs: "1.25rem", md: "1.5rem" },
//                       }}
//                     >
//                       Featured Topic
//                     </Typography>
//                   </Stack>
//                 </Box>

//                 <Box sx={{ p: { xs: 1.25, md: 1.5 } }}>
//                   <Stack spacing={1.5}>
//                     {featuredTopics.map((item) => (
//                       <Paper key={item.to} elevation={0} sx={{ ...innerPanelSx, p: 2 }}>
//                         <Stack spacing={1.1}>
//                           <Typography
//                             sx={{
//                               fontWeight: 700,
//                               color: style.color4,
//                               fontSize: "1.05rem",
//                               lineHeight: 1.4,
//                             }}
//                           >
//                             {item.title}
//                           </Typography>

//                           <Typography
//                             variant="body2"
//                             sx={{ color: style.color3, lineHeight: 1.65 }}
//                           >
//                             {item.description}
//                           </Typography>

//                           <Button
//                             component={RouterLink}
//                             to={item.to}
//                             sx={{
//                               alignSelf: "flex-start",
//                               borderRadius: "5px",
//                               textTransform: "none",
//                               fontWeight: 700,
//                               color: style.color4,
//                               backgroundColor: style.accentGreen,
//                               "&:hover": {
//                                 backgroundColor: style.accentGreen,
//                                 filter: "brightness(1.05)",
//                               },
//                             }}
//                           >
//                             Read Topic
//                           </Button>
//                         </Stack>
//                       </Paper>
//                     ))}

//                     <Divider sx={{ backgroundColor: "rgba(255,255,255,0.08)" }} />

//                     <Paper elevation={0} sx={{ ...subtlePanelSx, p: 1.5 }}>
//                       <Typography sx={{ color: style.color3, lineHeight: 1.7 }}>
//                         Looking for more? New learning content can be added over time as
//                         the library grows.
//                       </Typography>
//                     </Paper>
//                   </Stack>
//                 </Box>
//               </Paper>
//             </Box>

//             <Paper elevation={0} sx={outerPanelSx}>
//               <Box sx={{ p: { xs: 1.25, md: 1.5 } }}>
//                 <Stack
//                   direction={{ xs: "column", sm: "row" }}
//                   spacing={1.5}
//                   justifyContent="space-between"
//                   alignItems={{ xs: "flex-start", sm: "center" }}
//                 >
//                   <Typography sx={{ color: style.color3, lineHeight: 1.7 }}>
//                     You can also browse Parish Audio plugins or return to the home page.
//                   </Typography>

//                   <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
//                     <Button
//                       component={RouterLink}
//                       to="/plugins"
//                       sx={{
//                         borderRadius: "5px",
//                         textTransform: "none",
//                         fontWeight: 700,
//                         color: style.color4,
//                         backgroundColor: style.accentGreen,
//                         "&:hover": {
//                           backgroundColor: style.accentGreen,
//                           filter: "brightness(1.05)",
//                         },
//                       }}
//                     >
//                       Plugins
//                     </Button>

//                     <Button
//                       component={RouterLink}
//                       to="/"
//                       sx={{
//                         borderRadius: "5px",
//                         textTransform: "none",
//                         fontWeight: 700,
//                         color: style.color4,
//                         backgroundColor: "rgba(255,255,255,0.05)",
//                         border: "1px solid rgba(255,255,255,0.10)",
//                         "&:hover": {
//                           backgroundColor: "rgba(255,255,255,0.10)",
//                         },
//                       }}
//                     >
//                       Home
//                     </Button>
//                   </Stack>
//                 </Stack>
//               </Box>
//             </Paper>
//           </Stack>
//         </Box>
//       </Box>
//     </>
//   );
// }


// import {React, useState, useContext, useEffect, useRef} from "react"
// import {useStyle} from '../../context/styleContext/styleContext'
// import ReactGA from 'react-ga4';
// import { Paper } from '@mui/material';
// import { Container, Typography, Divider, Box, List, ListItem, ListItemText } from "@mui/material";


// export default function Learning(){

//   const { style, setStyle } = useStyle();
//   const [width, setWidth] = useState(window.innerWidth)

// function handleWindowSizeChange() {
//     setWidth(window.innerWidth);
// }
// useEffect(() => {
//     window.addEventListener('resize', handleWindowSizeChange);
//     return () => {
//         window.removeEventListener('resize', handleWindowSizeChange);
//     }
// }, []);

// const isMobile = width <= 768;

// useEffect(() => {
//   document.title = 'Parish Audio | Cutting-Edge Audio Processing | Learning Center';
// }, []);

// ReactGA.send({
//     hitType:"pageview",
//     page:"learning/delay",
//     title:"learning/delay",
//   })

//   const [height, setHeight] = useState(window.innerHeight)

//   useEffect(() => {
//     function handleResize() {      
//       setWidth(window.innerWidth)
//       setHeight(window.innerHeight)
//   }
  
//     window.addEventListener('resize', handleResize)
//   })

// const topContainer = useRef();
//     useEffect(() => {
//         // To make sure page starts from the top
//         topContainer.current.scrollIntoView({ block: "end" });
//     }, []);




//     return(
//         <>
//         <div style={{display:'flex', backgroundImage: `url("/RecordingStudioStockImage1.jpg")`, backgroundSize: "cover", backgroundPosition: "center", marginBottom:'0px', flex:1, minHeight:height-130, color:style.color4, padding:'0px'}}>
//         <div style={{display:'flex', flex:1}}>

//         <div className="dark-alpha-80" style={{flex:1}}>
// <div style={{width:'95%', maxWidth:'1000px', margin:'auto'}}>



//         <h1 ref={topContainer} style={{marginTop:'20px'}}>Learning Center</h1>

//         <Divider className="mobile-card-divider" />
//         <div style={{minHeight:height - 400}}>
//         <h2 style={{marginBottom:'20px'}}>Categories</h2>
//         <ul style={{}}>
//             <li style={{marginBottom:'10px'}}>
//                 <a href='/learning/chorus'>Chorus</a>
//             </li>
//             <li style={{marginBottom:'10px'}}>
//                 <a href='/learning/compression'>Compression</a>
//             </li>
//             <li style={{marginBottom:'10px'}}>
//                 <a href='/learning/delay'>Delay</a>
//             </li>
//             <li style={{marginBottom:'10px'}}>
//                 <a href='/learning/distortion'>Distortion</a>
//             </li>
//             <li style={{marginBottom:'10px'}}>
//                 <a href='/learning/equalization'>Equalization (EQ)</a>
//             </li>
//             <li style={{marginBottom:'10px'}}>
//                 <a href='/learning/overdrive'>Overdrive</a>
//             </li>
//             <li style={{marginBottom:'10px'}}>
//                 <a href='/learning/reverb'>Reverb</a>
//             </li>
//             <li style={{marginBottom:'10px'}}>
//                 <a href='/learning/reverb'>Reverb</a>
//             </li>
//         </ul>
//         </div>

// <h2>Topics:</h2>


// <ul style={{}}>
//             <li style={{marginBottom:'10px'}}>
//                 <a href='/learning/compare-distortion-overdrive-fuzz'>Overdrive vs Distortion vs Fuzz: What’s the Difference?</a>
//             </li>
//             </ul>

//         <Divider className="mobile-card-divider" />
//         <div style={{textAlign:"center"}}>
//         <a href="/home" style={{margin:'10px'}} >Home </a> 
//         <a href="/" style={{margin:'10px'}} >Plugins</a>
//         </div>

//   </div>
//   <div>

  
//       </div>
     

//       </div>

//       </div>
// </div>




        
//         </>

//     )
// }